/**
 * PlatformSelector Component
 *
 * Checkbox group for selecting target social media platforms
 * with compatibility warnings based on assets.
 */
import { Facebook, Instagram, Music2, AlertTriangle } from 'lucide-react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const PLATFORMS = [
  {
    id: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-200 dark:border-blue-800',
    supports: ['image', 'video', 'carousel'],
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-50 dark:bg-pink-900/30',
    borderColor: 'border-pink-200 dark:border-pink-800',
    supports: ['image', 'video', 'carousel'],
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    icon: Music2,
    color: 'text-black dark:text-white',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-200 dark:border-gray-700',
    supports: ['video'],
  },
];

const PlatformSelector = ({
  selected = [],
  onChange,
  compatibilityIssues = {},
  disabled = false,
  className,
}) => {
  const handleToggle = (platformId) => {
    if (disabled) return;

    if (selected.includes(platformId)) {
      onChange(selected.filter((p) => p !== platformId));
    } else {
      onChange([...selected, platformId]);
    }
  };

  return (
    <TooltipProvider>
      <div className={cn('space-y-2', className)}>
        <Label className="text-sm font-medium">Target Platforms</Label>
        <div className="flex flex-wrap gap-3">
          {PLATFORMS.map((platform) => {
            const Icon = platform.icon;
            const isSelected = selected.includes(platform.id);
            const issues = compatibilityIssues[platform.id] || [];
            const hasIssues = issues.length > 0;

            return (
              <Tooltip key={platform.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleToggle(platform.id)}
                    disabled={disabled}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all',
                      'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2',
                      isSelected
                        ? `${platform.bgColor} ${platform.borderColor} ${platform.color}`
                        : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400',
                      disabled && 'opacity-50 cursor-not-allowed',
                      hasIssues && isSelected && 'ring-2 ring-amber-400'
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggle(platform.id)}
                      disabled={disabled}
                      className="pointer-events-none"
                    />
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{platform.label}</span>
                    {hasIssues && isSelected && (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    )}
                  </button>
                </TooltipTrigger>
                {hasIssues && (
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-1">
                      <p className="font-medium text-amber-600 dark:text-amber-400">Compatibility Issues:</p>
                      <ul className="text-sm list-disc pl-4">
                        {issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </div>
        {selected.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Select at least one platform to publish to</p>
        )}
      </div>
    </TooltipProvider>
  );
};

export default PlatformSelector;
